import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';

const AuthContext = createContext(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Best-effort: invalidate the server-side session, then always drop the
  // local one regardless of whether the POST succeeds (contract: logout()).
  const logout = useCallback(() => {
    // Send the token explicitly. axios request interceptors run in a microtask, so the
    // removeItem below executes BEFORE the interceptor reads localStorage: the POST used to
    // go out unauthenticated, the server never incremented token_version, and the "revoked"
    // JWT stayed valid for its full ACCESS_TOKEN_EXPIRE_MINUTES after sign-out.
    const token = localStorage.getItem('token');
    localStorage.removeItem('token');
    setUser(null);
    if (token) {
      authAPI.logout({ headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    }
  }, []);

  const loadUser = useCallback(async () => {
    if (!localStorage.getItem('token')) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const response = await authAPI.getProfile();
      setUser(response.data);
    } catch (error) {
      // Stale or invalid token: same outcome as an explicit logout.
      logout();
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = async (email, password) => {
    try {
      const response = await authAPI.login({ email, password });
      const { access_token: accessToken, user: loggedInUser } = response.data;
      localStorage.setItem('token', accessToken);
      setUser(loggedInUser);
      return { success: true };
    } catch (error) {
      return { success: false, error: getApiErrorMessage(error, 'Login failed') };
    }
  };

  // Anshul, 2026-09-26: self-registration now waits for an admin's approval, so this
  // supersedes 19.2's "returns a LoginResponse and signs the caller in immediately" --
  // the 201 body is now { status: "pending", message }, no token, no user. Nothing is
  // stored and no one is signed in; RegisterPage shows `message` and sends the
  // applicant back to /login rather than into the app.
  const register = async ({ email, password, first_name: firstName, last_name: lastName }) => {
    try {
      const response = await authAPI.register({
        email,
        password,
        first_name: firstName,
        last_name: lastName,
      });
      return { success: true, message: response.data?.message };
    } catch (error) {
      return { success: false, error: getApiErrorMessage(error, 'Registration failed') };
    }
  };

  // Anshul, 2026-09-26 review item b: any signed-in user's self-service password
  // change (ProfilePage's "Change password" form). The response is a fresh
  // LoginResponse -- current_password already verified server-side, and
  // token_version was bumped there too, so the OLD token this request went out on
  // is now stale; the NEW one is what keeps this session logged in.
  const changePassword = async ({ currentPassword, newPassword }) => {
    try {
      const response = await authAPI.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });
      const { access_token: accessToken, user: updatedUser } = response.data;
      localStorage.setItem('token', accessToken);
      setUser(updatedUser);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: getApiErrorMessage(error, 'Could not change password'),
      };
    }
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
  };

  const value = {
    user,
    loading,
    isAuthenticated: Boolean(user),
    login,
    register,
    changePassword,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
